import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

export default class extends Controller {
  static targets = [
    "trendCanvas",
    "lastUpdated",
    "successRate",
    "syncsTotal",
    "syncsSucceeded",
    "syncsFailed",
    "activeConnections",
    "failedConnections",
    "awaitingReauth",
    "disabledConnections",
    "totalConnections"
  ]
  static values = {
    refreshUrl: String,
    refreshIntervalMs: { type: Number, default: 60000 },
    trend: String
  }

  connect() {
    this._chart = null
    this._initChart()
    this._startPolling()
  }

  disconnect() {
    this._stopPolling()
    if (this._chart) {
      this._chart.destroy()
      this._chart = null
    }
  }

  refreshNow() {
    this._fetchAndRender()
  }

  // ── Internal ──────────────────────────────────────────────────
  _startPolling() {
    this._stopPolling()
    if (!this.refreshUrlValue) return
    this._timer = setInterval(() => this._fetchAndRender(),
                              this.refreshIntervalMsValue)
  }

  _stopPolling() {
    if (this._timer) {
      clearInterval(this._timer)
      this._timer = null
    }
  }

  async _fetchAndRender() {
    if (document.hidden) return
    try {
      const res = await fetch(this.refreshUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return
      const data = await res.json()
      this._applyStats(data.stats || {})
    } catch (e) {
      // network blip; we'll try again next tick
    }
  }

  _applyStats(stats) {
    this._setTarget("activeConnections", stats.active_connections)
    this._setTarget("failedConnections", stats.failed_connections)
    this._setTarget("awaitingReauth", stats.awaiting_reauth)
    this._setTarget("disabledConnections", stats.disabled_connections)
    this._setTarget("totalConnections", stats.total_connections)
    this._setTarget("syncsTotal", stats.syncs_24h)
    this._setTarget("syncsSucceeded", stats.syncs_succeeded_24h)
    this._setTarget("syncsFailed", stats.syncs_failed_24h)
    if (this.hasSuccessRateTarget) {
      const rate = stats.sync_success_rate
      this.successRateTarget.textContent = (rate === null || rate === undefined) ? "—" : `${rate}%`
    }
    if (this.hasLastUpdatedTarget) {
      const now = new Date()
      this.lastUpdatedTarget.textContent = `Updated ${this._fmtTime(now)}`
    }
    if (Array.isArray(stats.sync_trend)) {
      this._updateChart(stats.sync_trend)
    }
  }

  _setTarget(name, value) {
    const camel = `${name}Target`
    if (!this[`has${camel.charAt(0).toUpperCase()}${camel.slice(1)}`]) return
    const el = this[camel]
    if (el && value !== undefined && value !== null) {
      el.textContent = String(value)
    }
  }

  _initChart() {
    if (!this.hasTrendCanvasTarget) return
    let trend = []
    try { trend = JSON.parse(this.trendValue || "[]") } catch (e) { trend = [] }
    if (!Array.isArray(trend) || trend.length === 0) return

    const labels = trend.map((b) => this._labelFromTs(b.ts))
    const success = trend.map((b) => b.success || 0)
    const failed = trend.map((b) => b.failed || 0)

    this._chart = new Chart(this.trendCanvasTarget, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Success",
            data: success,
            backgroundColor: "rgba(16,185,129,0.7)",
            borderRadius: 2
          },
          {
            label: "Failed",
            data: failed,
            backgroundColor: "rgba(244,63,94,0.7)",
            borderRadius: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => items[0]?.label || ""
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            ticks: { font: { size: 9 }, maxRotation: 0, autoSkipPadding: 12 },
            grid: { display: false }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: { font: { size: 9 }, precision: 0 },
            grid: { color: "rgba(0,0,0,0.04)" }
          }
        }
      }
    })
  }

  _updateChart(trend) {
    if (!this._chart) {
      // Canvas was hidden on first render or chart didn't init —
      // try once more now that we have data.
      this._initChart()
      return
    }
    this._chart.data.labels = trend.map((b) => this._labelFromTs(b.ts))
    this._chart.data.datasets[0].data = trend.map((b) => b.success || 0)
    this._chart.data.datasets[1].data = trend.map((b) => b.failed || 0)
    this._chart.update("none")
  }

  _labelFromTs(ts) {
    if (!ts) return ""
    // ts looks like "2026-05-01 14:00:00" — return "14:00".
    const m = ts.match(/(\d{2}):\d{2}:\d{2}$/)
    return m ? `${m[1]}:00` : ts
  }

  _fmtTime(date) {
    const h = String(date.getHours()).padStart(2, "0")
    const m = String(date.getMinutes()).padStart(2, "0")
    return `${h}:${m}`
  }
}
